// Validaciones frontend avanzadas

// Validar número de expediente
export const validateExpedienteNumero = (numero) => {
  if (!numero || numero.trim().length === 0) {
    return 'El número de expediente es obligatorio';
  }
  
  if (numero.trim().length > 50) {
    return 'El número de expediente no puede tener más de 50 caracteres';
  }
  
  // Validar formato básico (opcional)
  const formatoValido = /^[A-Z0-9\-_\/]+$/i;
  if (!formatoValido.test(numero.trim())) {
    return 'El número de expediente contiene caracteres no válidos';
  }
  
  return null;
};

// Validar monto
export const validateMonto = (monto) => {
  if (!monto || monto.toString().trim().length === 0) {
    return 'El monto es obligatorio';
  }
  
  const valor = parseFloat(monto);
  if (isNaN(valor)) {
    return 'El monto debe ser un número válido';
  }
  
  if (valor <= 0) {
    return 'El monto debe ser mayor a 0';
  }
  
  if (valor > 999999999.99) {
    return 'El monto no puede ser mayor a 999,999,999.99';
  }
  
  return null;
};

// Validar fecha límite
export const validateFechaLimite = (fecha) => {
  if (!fecha) {
    return 'La fecha límite es obligatoria';
  }
  
  const fechaLimite = new Date(fecha);
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  
  if (isNaN(fechaLimite.getTime())) {
    return 'La fecha no es válida';
  }
  
  if (fechaLimite < hoy) {
    return 'La fecha límite no puede ser anterior a hoy';
  }
  
  // Validar que no sea más de 10 años en el futuro
  const maxFecha = new Date();
  maxFecha.setFullYear(maxFecha.getFullYear() + 10);
  
  if (fechaLimite > maxFecha) {
    return 'La fecha límite no puede ser más de 10 años en el futuro';
  }
  
  return null;
};

// Validar URL
export const validateURL = (url) => {
  if (!url || url.trim().length === 0) {
    return null; // URL es opcional
  }
  
  try {
    new URL(url);
    return null;
  } catch {
    return 'La URL no es válida';
  }
};

const GOOGLE_MAPS_HOSTS = [
  'maps.google.com',
  'www.google.com',
  'google.com',
  'maps.app.goo.gl',
  'goo.gl'
];

/** Validación de enlace Google Maps (opcional u obligatorio según tipo EMS). */
export const validateGoogleMapsUrl = (url, { required = false } = {}) => {
  if (!url || url.trim() === '') {
    return required ? 'La ubicación en Google Maps es obligatoria' : null;
  }

  try {
    const parsed = new URL(url.trim());
    const host = parsed.hostname.toLowerCase().replace(/^www\./, '');
    const ok = GOOGLE_MAPS_HOSTS.some((h) => {
      const normalized = h.replace(/^www\./, '');
      return host === normalized || parsed.hostname.toLowerCase().endsWith(`.${normalized}`);
    });
    return ok ? null : 'Debe ser un enlace de Google Maps';
  } catch {
    return 'La URL no es válida';
  }
};

// Validar celular
export const validateCelular = (celular) => {
  if (!celular || celular.trim().length === 0) {
    return 'El celular es obligatorio';
  }
  
  const celularLimpio = celular.replace(/[\s\-\(\)]/g, '');
  
  if (celularLimpio.length < 10) {
    return 'El celular debe tener al menos 10 dígitos';
  }
  
  if (celularLimpio.length > 15) {
    return 'El celular no puede tener más de 15 dígitos';
  }
  
  if (!/^\d+$/.test(celularLimpio)) {
    return 'El celular solo puede contener números';
  }
  
  return null;
};

// Validar nombre
export const validateNombre = (nombre) => {
  if (!nombre || nombre.trim().length === 0) {
    return 'El nombre es obligatorio';
  }
  
  if (nombre.trim().length < 2) {
    return 'El nombre debe tener al menos 2 caracteres';
  }
  
  if (nombre.trim().length > 255) {
    return 'El nombre no puede tener más de 255 caracteres';
  }
  
  // Validar que solo contenga letras, espacios y algunos caracteres especiales
  const formatoValido = /^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s\-\.]+$/;
  if (!formatoValido.test(nombre.trim())) {
    return 'El nombre contiene caracteres no válidos';
  }
  
  return null;
};

// Validar descripción
export const validateDescripcion = (descripcion, maxLength = 500) => {
  if (!descripcion || descripcion.trim().length === 0) {
    return null; // Descripción es opcional
  }
  
  if (descripcion.trim().length > maxLength) {
    return `La descripción no puede tener más de ${maxLength} caracteres`;
  }
  
  return null;
};

// Validar archivo
export const validateFile = (file, maxSize = 10 * 1024 * 1024, allowedTypes = []) => {
  if (!file) {
    return 'Debe seleccionar un archivo';
  }
  
  // Validar tamaño
  if (file.size > maxSize) {
    const maxSizeMB = maxSize / (1024 * 1024);
    return `El archivo no puede ser mayor a ${maxSizeMB}MB`;
  }
  
  // Validar tipo de archivo
  if (allowedTypes.length > 0) {
    const extension = file.name.split('.').pop().toLowerCase();
    if (!allowedTypes.includes(extension)) {
      return `Solo se permiten archivos: ${allowedTypes.join(', ')}`;
    }
  }
  
  return null;
};

// Validar múltiples archivos
export const validateFiles = (files, maxSize = 10 * 1024 * 1024, allowedTypes = []) => {
  if (!files || files.length === 0) {
    return 'Debe seleccionar al menos un archivo';
  }
  
  if (files.length > 10) {
    return 'No puede seleccionar más de 10 archivos a la vez';
  }
  
  for (let i = 0; i < files.length; i++) {
    const error = validateFile(files[i], maxSize, allowedTypes);
    if (error) {
      return `Archivo ${files[i].name}: ${error}`;
    }
  }
  
  return null;
};

// Validar formulario completo de expediente
export const validateExpedienteForm = (formData) => {
  const errors = {};
  
  // Validar número
  const numeroError = validateExpedienteNumero(formData.numero);
  if (numeroError) errors.numero = numeroError;
  
  // Validar cliente
  if (!formData.clienteId) {
    errors.clienteId = 'Debe seleccionar un cliente';
  }
  
  // Validar tipo
  if (!formData.tipoId) {
    errors.tipoId = 'Debe seleccionar un tipo de expediente';
  }
  
  // Validar monto
  const montoError = validateMonto(formData.montoTotal);
  if (montoError) errors.montoTotal = montoError;
  
  // Validar fecha límite
  const fechaError = validateFechaLimite(formData.fechaLimite);
  if (fechaError) errors.fechaLimite = fechaError;
  
  // Validar URL (opcional salvo EMS tipo 5)
  const esEMS = parseInt(formData.tipoId, 10) === 5;
  const mapsError = validateGoogleMapsUrl(formData.ubicacionUrl, { required: esEMS });
  if (mapsError) {
    errors.ubicacionUrl = mapsError;
  } else if (!esEMS) {
    const urlError = validateURL(formData.ubicacionUrl);
    if (urlError) errors.ubicacionUrl = urlError;
  }
  
  // Validar descripción (opcional)
  const descripcionError = validateDescripcion(formData.descripcion);
  if (descripcionError) errors.descripcion = descripcionError;
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

// Validar formulario de cliente
export const validateClienteForm = (formData) => {
  const errors = {};
  
  // Validar nombre
  const nombreError = validateNombre(formData.nombre);
  if (nombreError) errors.nombre = nombreError;
  
  // Validar celular
  const celularError = validateCelular(formData.celular);
  if (celularError) errors.celular = celularError;
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

// Validar formulario de tipo
export const validateTipoForm = (formData) => {
  const errors = {};
  
  // Validar nombre
  if (!formData.nombre || formData.nombre.trim().length === 0) {
    errors.nombre = 'El nombre es obligatorio';
  } else if (formData.nombre.trim().length > 100) {
    errors.nombre = 'El nombre no puede tener más de 100 caracteres';
  }
  
  // Validar descripción (opcional)
  const descripcionError = validateDescripcion(formData.descripcion, 500);
  if (descripcionError) errors.descripcion = descripcionError;
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

// Validar formulario de parte
export const validateParteForm = (formData) => {
  const errors = {};
  
  // Validar descripción
  if (!formData.descripcion || formData.descripcion.trim().length === 0) {
    errors.descripcion = 'La descripción es obligatoria';
  } else if (formData.descripcion.trim().length > 1000) {
    errors.descripcion = 'La descripción no puede tener más de 1000 caracteres';
  }
  
  // Validar fecha límite
  const fechaError = validateFechaLimite(formData.fechaLimite);
  if (fechaError) errors.fechaLimite = fechaError;
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

// Validar formulario de abono
export const validateAbonoForm = (formData) => {
  const errors = {};
  
  // Validar fecha
  if (!formData.fecha) {
    errors.fecha = 'La fecha es obligatoria';
  } else {
    const fechaAbono = new Date(formData.fecha);
    if (isNaN(fechaAbono.getTime())) {
      errors.fecha = 'La fecha no es válida';
    }
  }
  
  // Validar monto
  const montoError = validateMonto(formData.monto);
  if (montoError) errors.monto = montoError;
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

// Función para mostrar errores de validación
export const showValidationErrors = (errors) => {
  const errorMessages = Object.values(errors);
  if (errorMessages.length === 1) {
    return errorMessages[0];
  } else if (errorMessages.length > 1) {
    return `Errores de validación:\n${errorMessages.join('\n')}`;
  }
  return null;
};

// Función para limpiar errores de validación
export const clearValidationErrors = (setErrors) => {
  setErrors({});
};

// Función para validar en tiempo real
export const validateField = (value, validator, setFieldError) => {
  const error = validator(value);
  setFieldError(error);
  return !error;
};








